import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../model/user.model';
import {ContactInfos} from '../../model/contact-infos.model';
import {UserInteractor} from '../../interactors/user.interactor';
import {LoginInteractor} from '../../interactors/login.interactor';
import {Keys, StorageService} from '../../service/storage.service';
import {AnalyticsTracker} from '../../utils/AnalyticsTracker';
import {MatButtonToggleChange} from '@angular/material';
import {Scheduler} from '../../utils/Scheduler';
import {Toaster, Type} from '../../toaster/Toaster';
import {executeWithLoader, LoaderStatus} from '../../utils/loader.util';

@Component({
    templateUrl: './borrower-phone-number-verification.component.html',
    styleUrls: ['./borrower-phone-number-verification.component.scss']
})
export class BorrowerPhoneNumberVerificationComponent implements OnInit {
    private readonly activatedRoute: ActivatedRoute;
    private readonly userInteractor: UserInteractor;
    private readonly loginInteractor: LoginInteractor;
    private readonly storageService: StorageService;
    private readonly analyticsTracker: AnalyticsTracker;
    private readonly router: Router;
    private scheduler: Scheduler;
    user: User;
    contactInfos: ContactInfos;
    loginId: string;
    otp: string;
    otpGenerated: boolean;
    resendOtp: boolean;
    public loaderStatus: LoaderStatus = {isLoading: false};

    constructor(activatedRoute: ActivatedRoute, userInteractor: UserInteractor, loginInteractor: LoginInteractor,
                storageService: StorageService, analyticsTracker: AnalyticsTracker, router: Router,
                scheduler: Scheduler) {
        this.activatedRoute = activatedRoute;
        this.userInteractor = userInteractor;
        this.loginInteractor = loginInteractor;
        this.storageService = storageService;
        this.analyticsTracker = analyticsTracker;
        this.router = router;
        this.scheduler = scheduler;
    }

    async ngOnInit(): Promise<void> {
        this.activatedRoute.data.subscribe((data) => {
            this.user = data.user;
        });
        this.contactInfos = await this.userInteractor.getContactInfosByUserId(this.user._id);
        if (!this.hasMultiplePhoneNumber()) {
            this.loginId = this.contactInfos[0].phoneNumber;
            await this.sendOtp();
        }
    }

    getIdentityFileId(): string {
        return this.user.identity && this.user.identity.proofs && this.user.identity.proofs[0] && this.user.identity.proofs[0].fileIds[0];
    }

    async sendOtp() {
        this.disableResendOtp();
        try {
            const {id, maskedPhoneNumber} = await this.loginInteractor.requestVerification(this.loginId, this.user._id);
            this.storageService.saveItem(Keys.LOGIN_TOKEN, id);
            Toaster.toast(`An OTP has been sent to ${maskedPhoneNumber}`, Type.Success);
            this.setOtpGenerated(true);
            this.disableResendOtpFor();
        } catch (error) {
            if (error.status === 429) {
                Toaster.toast('You have requested for the verification code too many times. Please wait for some time before trying again.', Type.Error);
            } else {
                Toaster.toast('Something went wrong. Please try again later.', Type.Error);
            }
            this.enableResendOtp();
        }
    }

    async verifyOtp() {
        await executeWithLoader(this.loaderStatus, async () => {
            const user = await this.login(this.otp);
            this.analyticsTracker.setUserId();
            this.analyticsTracker.sendAuthenticationEvent();
            this.storageService.saveItem(Keys.BORROWER, 'true');
            await this.userInteractor.updatePhoneNumberAndMarkOtpAsVerified(user._id, this.loginId);
            await this.userInteractor.markContactAsPrimary(user._id, this.loginId);
            await this.router.navigate(['/borrow/dashboard/applications']);
        });
    }

    private async login(password): Promise<any> {
        const loginRequestId = this.storageService.getItem(Keys.LOGIN_TOKEN);
        return this.loginInteractor.verifyPassword(loginRequestId, password);
    }

    onPhoneNumberSelection(event: MatButtonToggleChange) {
        this.loginId = event.value;
        this.setOtpGenerated(false);
    }

    hasMultiplePhoneNumber(): boolean {
        return this.contactInfos.length > 1;
    }

    enableGetOtpButton() {
        return this.loginId && this.otpGenerated === false;
    }

    setOtpGenerated(input: boolean) {
        this.otpGenerated = input;
    }

    isOtpGenerated(): boolean {
        return this.otpGenerated === true;
    }

    public enableResendOtp = () => {
        this.resendOtp = true;
    };

    public disableResendOtp = () => {
        this.resendOtp = false;
    };

    isResendEnabled() {
        return this.resendOtp === true;
    }

    private disableResendOtpFor() {
        this.scheduler.scheduleCallback(1000 * 30, this.enableResendOtp);
    }
}
